import React, { useState, useEffect, Suspense, lazy } from "react";
import "twin.macro";
import { useDebounce } from "use-debounce";

import dayjs from "dayjs";

import { Event } from "api/v2/event.types";

import { FormSection, Legend, Description } from "../styles";

import { Alert, Button, P } from "components/typography";
import Input from "components/forms/input";
import Spinner from "components/spinner";

// Lazy-loaded so @react-google-maps/api ships in its own chunk and is
// only fetched when the user actually opens a D2D delivery form.
const D2dMap = lazy(() => import("./d2dMap"));

const MILES_TO_METERS = 1609.34;
const DISTANCE_BUFFER = 0.5 * MILES_TO_METERS;
const DEFAULT_RANGE_IN_MILES = 3;

export const fieldsToAddress = (
  street: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined
) =>
  !(street && zip)
    ? "" // require street and zip, at least
    : [
        [street, city].filter((i) => i).join(", "),
        [state, zip].filter((i) => i).join(" ")
      ]
        .filter((i) => i)
        .join(", ");

const addressFieldNames = [
  "deliveryStreet",
  "deliveryCity",
  "deliveryState",
  "deliveryPostalCode"
];

const D2dFormSection: React.FC<{
  event: Event;
  register: any;
  errors: any;
  watch: any;
  trigger: any;
}> = ({ event, register, errors, watch, trigger }) => {
  const rangeInMiles = event?.d2dRadius || DEFAULT_RANGE_IN_MILES;
  const watchedAddress = watch(addressFieldNames);
  const [deliveryAddress, setDeliveryAddress] = useState(
    fieldsToAddress(
      watchedAddress.deliveryStreet,
      watchedAddress.deliveryCity,
      watchedAddress.deliveryState,
      watchedAddress.deliveryPostalCode
    )
  );
  const [debouncedDeliveryAddress] = useDebounce(deliveryAddress, 500);
  const [deliveryDistance, setDeliveryDistance] = useState<
    number | undefined
  >();
  const validateDistance = () => {
    return (
      deliveryDistance === undefined ||
      deliveryDistance < rangeInMiles * MILES_TO_METERS + DISTANCE_BUFFER
    );
  };

  const processZip = (zip: React.ChangeEvent<HTMLInputElement>) => {
    if (zip.target.value.length == 5) {
      setDeliveryAddress(
        fieldsToAddress(
          watchedAddress.deliveryStreet,
          watchedAddress.deliveryCity,
          watchedAddress.deliveryState,
          zip.target.value
        )
      );
    }
  };

  useEffect(() => {
    trigger(addressFieldNames);
  }, [deliveryDistance]);

  // Helper to validate coordinate values
  const isValidCoord = (val: string | undefined): val is string => {
    if (!val) return false;
    const num = parseFloat(val);
    return !isNaN(num) && isFinite(num);
  };

  const coordinates = event.coordinates
    .map(({ lat, lng, ...rest }) => ({
      ...rest,
      lat: lat ? parseFloat(lat) : NaN,
      lng: lng ? parseFloat(lng) : NaN
    }))
    .filter(
      (coord) =>
        !isNaN(coord.lat) &&
        !isNaN(coord.lng) &&
        isFinite(coord.lat) &&
        isFinite(coord.lng)
    );

  const validationText = (
    <>
      Your address must be within {rangeInMiles} miles of our location
      {coordinates.length > 1 ? "s" : ""} to receive your order.
    </>
  );

  // Extract to local variables - enables TypeScript to track type narrowing
  const pickupLat = event.pickupLat;
  const pickupLng = event.pickupLng;

  // Determine map center - use pickup coords if valid, otherwise fallback to first delivery zone
  const hasValidPickup = isValidCoord(pickupLat) && isValidCoord(pickupLng);
  const mapCenter = hasValidPickup
    ? { lat: parseFloat(pickupLat), lng: parseFloat(pickupLng) }
    : coordinates[0]; // fallback to first delivery zone

  const pickupAddress = hasValidPickup
    ? `${event.pickupStreet}, ${event.pickupCityStateZip}`
    : undefined;

  return (
    <>
      <FormSection>
        <Description>
          <Legend>Delivery Location</Legend>
        </Description>

        <div tw="w-full md:w-3/4">
          <Alert.Info>{validationText}</Alert.Info>
          {mapCenter && (
            <div tw="py-6">
              <Suspense fallback={<Spinner disabled={false} />}>
                <D2dMap
                  radius={event.d2dRadius}
                  coordinates={coordinates}
                  lat={mapCenter.lat}
                  lng={mapCenter.lng}
                  fromAddress={pickupAddress}
                  deliveryAddress={debouncedDeliveryAddress}
                  setDeliveryDistance={setDeliveryDistance}
                />
              </Suspense>
            </div>
          )}
          <div tw="mt-2 flex flex-col gap-2">
            {deliveryDistance !== undefined &&
              (validateDistance() ? (
                <Alert.Success>
                  Your address is within our delivery range! We can't wait to
                  deliver you your amazing order.
                </Alert.Success>
              ) : (
                <Alert.Danger>
                  Your address is{" "}
                  {(
                    (deliveryDistance || 0) / MILES_TO_METERS -
                    rangeInMiles
                  ).toFixed(2)}{" "}
                  miles outside our delivery range. Sorry, we can't deliver your
                  order.
                </Alert.Danger>
              ))}
            <Input
              name={`deliveryStreet`}
              id={`deliveryStreet`}
              ref={register({
                required: "Must put in a street address",
                validate: {
                  distance: () =>
                    validateDistance() || "Address is outside of delivery range"
                }
              })}
              error={
                errors["deliveryStreet"] && errors["deliveryStreet"].message
              }
              type="text"
              tw="w-full"
              aria-label="Street"
              placeholder="Street"
              onBlur={(e) =>
                setDeliveryAddress(
                  fieldsToAddress(
                    e.target.value,
                    watchedAddress.deliveryCity,
                    watchedAddress.deliveryState,
                    watchedAddress.deliveryPostalCode
                  )
                )
              }
            />
            <div tw="flex flex-row gap-2">
              <Input
                name={`deliveryCity`}
                id={`deliveryCity`}
                ref={register({
                  required: "Must put in a city",
                  validate: {
                    distance: () =>
                      validateDistance() ||
                      "Address is outside of delivery range"
                  }
                })}
                error={errors["deliveryCity"] && errors["deliveryCity"].message}
                type="text"
                tw="w-full"
                aria-label="City"
                placeholder="City"
                onBlur={(e) =>
                  setDeliveryAddress(
                    fieldsToAddress(
                      watchedAddress.deliveryStreet,
                      e.target.value,
                      watchedAddress.deliveryState,
                      watchedAddress.deliveryPostalCode
                    )
                  )
                }
              />
              <Input
                name={`deliveryState`}
                id={`deliveryState`}
                ref={register({
                  required: "Must put in a state",
                  validate: {
                    distance: () =>
                      validateDistance() ||
                      "Address is outside of delivery range"
                  }
                })}
                error={
                  errors["deliveryState"] && errors["deliveryState"].message
                }
                type="text"
                tw="w-full"
                aria-label="State"
                placeholder="State"
                onBlur={(e) =>
                  setDeliveryAddress(
                    fieldsToAddress(
                      watchedAddress.deliveryStreet,
                      watchedAddress.deliveryCity,
                      e.target.value,
                      watchedAddress.deliveryPostalCode
                    )
                  )
                }
              />
              <Input
                name={`deliveryPostalCode`}
                id={`deliveryPostalCode`}
                ref={register({
                  required: "Must put in a zip code",
                  validate: {
                    distance: () =>
                      validateDistance() ||
                      "Address is outside of delivery range"
                  }
                })}
                error={
                  errors["deliveryPostalCode"] &&
                  errors["deliveryPostalCode"].message
                }
                type="text"
                tw="w-full"
                aria-label="Zip Code"
                placeholder="Zip Code"
                onChange={(e) => processZip(e)}
              />
            </div>
          </div>
          <div tw="flex flex-row gap-1 mt-2">
            <input
              id="default-address"
              name="saveDefaultAddress"
              type="checkbox"
              ref={register()}
              tw="accent-primary"
            />
            <P.Small as="label" htmlFor="default-address">
              Save as my default address
            </P.Small>
          </div>
        </div>
      </FormSection>
      <FormSection tw="md:items-center">
        <Description>
          <Legend>Delivery Time Window</Legend>
        </Description>
        <div tw="md:w-3/4">
          <Button.Primary tw="py-3 px-4" onClick={(e) => e.preventDefault()}>
            {dayjs(event.deliveryWindowLocal[0]).format("h:mm A")} -{" "}
            {dayjs(event.deliveryWindowLocal[1]).format("h:mm A")}
          </Button.Primary>
        </div>
      </FormSection>
    </>
  );
};

export default D2dFormSection;
